/**
 * Step sub-components for HrLeaveHistoryImportPage.
 *
 * Mirrors hr-leave-import/LeaveImportSteps so the two imports share the same
 * validation → verification → dry-run flow. Reuses the button helpers from the
 * balance-import steps.
 */

import { useRef, useState } from 'react'
import { VISUALLY_HIDDEN } from '../../lib/visually-hidden'
import { primaryBtn, secondaryBtn } from '../hr-leave-import/LeaveImportSteps'
import {
  parseHistoryCsv,
  importLeaveHistory,
  LEAVE_HISTORY_TEMPLATE_CSV,
  type HistoryImportResult,
  type HistoryImportRow,
} from '../../api/hr-leave-history'
import { HistoryImportPreviewTable } from './HistoryImportPreviewTable'

// ── Shared style helpers ──────────────────────────────────────────────────────

function alertBox(variant: 'danger' | 'warning' | 'info'): React.CSSProperties {
  const map = {
    danger: { background: 'var(--danger-bg)', color: 'var(--danger-fg)', border: '1px solid var(--danger-line)' },
    warning: {
      background: 'var(--warning-bg, var(--danger-bg))',
      color: 'var(--warning-fg, var(--danger-fg))',
      border: '1px solid var(--warning-line, var(--danger-line))',
    },
    info: { background: 'var(--primary-bg)', color: 'var(--steel-blue)', border: '1px solid var(--border)' },
  } as const
  return { ...map[variant], padding: '12px 16px', borderRadius: 8, fontSize: 13 }
}

function fmt(n: string): string {
  return n.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
}

function downloadTemplate(): void {
  const blob = new Blob([LEAVE_HISTORY_TEMPLATE_CSV], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'leave-history-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// ── Step 1: Prepare ───────────────────────────────────────────────────────────

interface PrepareStepProps {
  onNext: () => void
}

export function PrepareStep({ onNext }: PrepareStepProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text)' }}>
          This wizard brings each employee's past leave records from your old system into Leave Hub.
          Nothing changes until you click <strong>Import</strong> at the end — you get a full
          preview first.
        </p>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)' }}>
          Export the leave-history report from your old system as a CSV, then upload it here. After
          import, Leave Hub recalculates everyone's <strong>used</strong> and <strong>pending</strong>{' '}
          days straight from these records.
        </p>
      </div>

      <div role="note" style={alertBox('info')}>
        <strong>Run “Set entitlements” first.</strong> That sets each person's yearly entitlement;
        this import fills in how much they have taken against it. Buckets without an entitlement
        yet are flagged in the preview and not changed.
      </div>

      {/* Column guide */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Your CSV needs these columns (in any order, extra columns are fine):
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
          {(
            [
              { col: 'Start Date', desc: 'The leave date, day-first (e.g. 7/8/2026 = 7 Aug 2026)' },
              { col: 'End Date', desc: 'Same as Start Date for a single day' },
              { col: 'Employee No.', desc: "HR's staff number — used to match the person in Leave Hub" },
              { col: 'Employee Name', desc: 'Full name — a cross-check only, not used for matching' },
              { col: 'Leave Type', desc: 'Exactly as shown in your old system (e.g. ANNUAL LEAVE)' },
              { col: 'Leave Period', desc: 'Full Day, 1st Half Day, or 2nd Half Day' },
              { col: 'Day(s)', desc: 'How many days this row counts (e.g. 1.00 or 0.50)' },
              { col: 'Status', desc: 'Approved counts as taken; Pending is still awaiting a decision' },
              { col: 'Request ID', desc: 'Optional — kept as a note on the imported record' },
            ] as const
          ).map(({ col, desc }, i) => (
            <div
              key={col}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)',
              }}
            >
              <div
                style={{
                  padding: '8px 12px',
                  fontFamily: 'var(--font-mono, monospace)',
                  fontWeight: 600,
                  color: 'var(--text)',
                  borderRight: '1px solid var(--border)',
                }}
              >
                {col}
              </div>
              <div style={{ padding: '8px 12px', color: 'var(--text-2)' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Gotchas callout */}
      <div role="note" style={alertBox('warning')}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Two things to check before uploading</div>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
          <li>
            <strong>Dates are day-first.</strong> 7/8/2026 means 7 August, not 8 July. Make sure
            your export uses the same order.
          </li>
          <li>
            <strong>Approved vs Pending matters.</strong> Approved rows become taken days; Pending
            rows are held aside as pending. Rejected and cancelled rows don't affect balances.
          </li>
        </ul>
      </div>

      {/* Template download */}
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
            Not sure about the column names?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Download our template — it shows the expected headers with sample rows.
          </div>
        </div>
        <button type="button" onClick={downloadTemplate} style={secondaryBtn()}>
          Download template
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onNext} style={primaryBtn()}>
          Next: upload your file →
        </button>
      </div>
    </div>
  )
}

// ── Step 2: Upload ────────────────────────────────────────────────────────────

interface UploadStepProps {
  onPreviewReady: (rows: HistoryImportRow[], result: HistoryImportResult) => void
  onBack: () => void
}

export function UploadStep({ onPreviewReady, onBack }: UploadStepProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [parseErrors, setParseErrors] = useState<Array<{ line: number; message: string }>>([])
  const [serverError, setServerError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [typeError, setTypeError] = useState<string | null>(null)

  function acceptFile(file: File): void {
    const name = file.name.toLowerCase()
    const looksCsv =
      name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel'
    if (!looksCsv) {
      setTypeError(`"${file.name}" is not a CSV file. Please use a .csv export.`)
      return
    }
    setTypeError(null)
    handleFile(file)
  }

  function handleFile(file: File): void {
    setParseErrors([])
    setServerError(null)
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = typeof e.target?.result === 'string' ? e.target.result : ''
      const { rows, errors } = parseHistoryCsv(text)

      const structureError = errors.find((err) => err.line === 0)
      if (structureError != null) {
        setParseErrors(errors)
        return
      }
      if (errors.length > 0) setParseErrors(errors)
      if (rows.length === 0) return

      setBusy(true)
      importLeaveHistory(rows, true)
        .then((result) => onPreviewReady(rows, result))
        .catch((err: unknown) =>
          setServerError(err instanceof Error ? err.message : 'Server error — please try again.'),
        )
        .finally(() => setBusy(false))
    }
    reader.readAsText(file)
  }

  const hasStructureError = parseErrors.some((e) => e.line === 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 14, color: 'var(--text-2)' }}>
        Drag your CSV file here or click to choose. Leave Hub will check it and show you a preview before
        anything is saved.
      </div>

      <label
        onDragEnter={(e) => {
          e.preventDefault()
          if (!busy) setDragging(true)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!busy && !dragging) setDragging(true)
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (busy) return
          const file = e.dataTransfer.files?.[0]
          if (file != null) acceptFile(file)
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          border: `2px dashed ${dragging ? 'var(--steel-blue)' : 'var(--border)'}`,
          borderRadius: 12,
          padding: '40px 24px',
          textAlign: 'center',
          cursor: busy ? 'not-allowed' : 'pointer',
          background: dragging ? 'var(--primary-bg)' : 'var(--surface)',
          transition: 'background 120ms var(--motion-ease-out), border-color 120ms var(--motion-ease-out)',
        }}
      >
        <span style={{ fontSize: 32 }}>📂</span>
        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
          {busy
            ? 'Checking your file…'
            : dragging
              ? 'Drop your CSV file to upload'
              : 'Drag a CSV file here, or click to choose'}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>
          Accepts .csv files. Columns can be in any order.
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          tabIndex={-1}
          style={VISUALLY_HIDDEN}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file != null) acceptFile(file)
            e.target.value = ''
          }}
        />
      </label>

      {typeError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Unsupported file</div>
          {typeError}
        </div>
      ) : null}

      {hasStructureError ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>This file is missing required columns</div>
          <p style={{ margin: '0 0 6px' }}>{parseErrors[0]?.message}</p>
          <p style={{ margin: 0, opacity: 0.85 }}>
            Go back to Step 1 and download the template to see the expected column names.
          </p>
        </div>
      ) : parseErrors.length > 0 ? (
        <div role="alert" style={alertBox('warning')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {parseErrors.length} row{parseErrors.length === 1 ? '' : 's'} skipped before upload
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {parseErrors.map((err) => (
              <li key={err.line}>
                Line {err.line}: {err.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {serverError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Could not check the file</div>
          {serverError}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <button type="button" onClick={onBack} style={secondaryBtn()} disabled={busy}>
          ← Back
        </button>
      </div>
    </div>
  )
}

// ── Step 3: Preview ───────────────────────────────────────────────────────────

interface PreviewStepProps {
  rows: HistoryImportRow[]
  preview: HistoryImportResult
  skipped: ReadonlySet<number>
  onToggleSkip: (rowIndex: number) => void
  onBack: () => void
  onNext: (balancesPreview: HistoryImportResult, keptRows: HistoryImportRow[]) => void
}

export function PreviewStep({
  rows,
  preview,
  skipped,
  onToggleSkip,
  onBack,
  onNext,
}: PreviewStepProps): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const kept = preview.rows.filter((r) => !skipped.has(r.rowIndex))
  const newCount = kept.filter((r) => r.action === 'create').length
  const alreadyCount = kept.filter((r) => r.action === 'skip').length
  const errorRows = preview.rows.filter((r) => r.action === 'error')
  const errorCount = errorRows.length

  // The common error is "this person isn't in LMS yet". Group those repetitive
  // rows by staff number so HR sees "2 people missing", not 21 identical lines.
  const NOT_FOUND_PREFIX = 'No employee in Leave Hub'
  const notFound = errorRows.filter((r) => r.error?.startsWith(NOT_FOUND_PREFIX))
  const otherErrors = errorRows.filter((r) => !r.error?.startsWith(NOT_FOUND_PREFIX))
  const notFoundByEmp = new Map<string, { name: string; count: number }>()
  for (const r of notFound) {
    const cur = notFoundByEmp.get(r.employeeNo) ?? { name: r.csvEmployeeName, count: 0 }
    notFoundByEmp.set(r.employeeNo, { name: cur.name, count: cur.count + 1 })
  }

  function handleNext(): void {
    setBusy(true)
    setServerError(null)
    // Re-run the dry-run on exactly the rows HR kept, so the next step's balance
    // totals reflect any skips.
    const keptRows = rows.filter((_, idx) => !skipped.has(idx))
    importLeaveHistory(keptRows, true)
      .then((result) => onNext(result, keptRows))
      .catch((err: unknown) => {
        setServerError(err instanceof Error ? err.message : 'Server error — please try again.')
        setBusy(false)
      })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary */}
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          fontSize: 14,
          color: 'var(--text)',
          lineHeight: 1.6,
        }}
      >
        <strong>{newCount}</strong> leave record{newCount === 1 ? '' : 's'} will be imported,{' '}
        <strong>{alreadyCount}</strong> already present,{' '}
        <strong>{skipped.size}</strong> skipped by you
        {errorCount > 0 ? (
          <>
            , <strong style={{ color: 'var(--danger-fg)' }}>{errorCount}</strong> need
            {errorCount === 1 ? 's' : ''} your attention
          </>
        ) : null}
        . Review the balance impact on the next step before importing.
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-3)' }}>
        {(
          [
            { label: 'New', bg: 'var(--success-bg)', color: 'var(--success-fg)', note: '— will be imported' },
            { label: 'Already in', bg: 'var(--surface-2)', color: 'var(--text-3)', note: '— already in Leave Hub, skipped' },
            { label: 'Error', bg: 'var(--danger-bg)', color: 'var(--danger-fg)', note: '— fix the row before importing' },
          ] as const
        ).map(({ label, bg, color, note }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color }}>
              {label}
            </span>
            <span>{note}</span>
          </span>
        ))}
      </div>

      {/* Per-row verification table — the primary visual, matching Set entitlements */}
      <HistoryImportPreviewTable rows={preview.rows} skipped={skipped} onToggleSkip={onToggleSkip} />

      {notFoundByEmp.size > 0 ? (
        <div role="note" style={alertBox('warning')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {notFoundByEmp.size} {notFoundByEmp.size === 1 ? 'person is' : 'people are'} not in Leave Hub
            yet — their {notFound.length} record{notFound.length === 1 ? '' : 's'} can't be imported
          </div>
          <ul style={{ margin: '0 0 6px', paddingLeft: 20, lineHeight: 1.6 }}>
            {[...notFoundByEmp.entries()].map(([no, { name, count }]) => (
              <li key={no}>
                <strong>{name?.trim() || 'Unknown'}</strong> (staff no. {no}) — {count} record
                {count === 1 ? '' : 's'}
              </li>
            ))}
          </ul>
          <div style={{ opacity: 0.9 }}>
            Add these people first via <strong>Bulk import</strong> (or create them), then come back
            and re-import — already-imported records are skipped. Or import now and these are left
            out.
          </div>
        </div>
      ) : null}

      {otherErrors.length > 0 ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {otherErrors.length} row{otherErrors.length === 1 ? '' : 's'} could not be read
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {otherErrors.slice(0, 20).map((r) => (
              <li key={r.rowIndex}>
                Row {r.rowIndex + 1} ({r.employeeNo}): {r.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {serverError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Could not continue</div>
          {serverError}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={secondaryBtn()} disabled={busy}>
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={busy || newCount === 0}
          style={primaryBtn(busy || newCount === 0)}
        >
          {busy ? 'Checking…' : 'Next: review balances →'}
        </button>
      </div>
    </div>
  )
}

// ── Step 4: Review balances ─────────────────────────────────────────────────────

interface BalancesStepProps {
  /** Dry-run computed on exactly the kept rows, so totals reflect any skips. */
  preview: HistoryImportResult
  keptRows: HistoryImportRow[]
  onBack: () => void
  onCommit: (result: HistoryImportResult) => void
}

export function BalancesStep({ preview, keptRows, onBack, onCommit }: BalancesStepProps): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const missingBalance = preview.bucketUpdates.filter((b) => !b.balanceFound)

  const cell: React.CSSProperties = { padding: '6px 10px', borderTop: '1px solid var(--border)', fontSize: 13 }
  const th: React.CSSProperties = { padding: '6px 10px', textAlign: 'left', fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }

  function handleCommit(): void {
    setBusy(true)
    setServerError(null)
    importLeaveHistory(keptRows, false)
      .then((result) => onCommit(result))
      .catch((err: unknown) => {
        setServerError(err instanceof Error ? err.message : 'Import failed — please try again.')
        setBusy(false)
      })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          fontSize: 14,
          color: 'var(--text)',
          lineHeight: 1.6,
        }}
      >
        Importing <strong>{preview.wouldCreate}</strong> record
        {preview.wouldCreate === 1 ? '' : 's'} will set the balances below.{' '}
        <strong>{preview.bucketUpdates.length}</strong> bucket
        {preview.bucketUpdates.length === 1 ? '' : 's'} will change. Used and pending are
        recalculated entirely from the imported records (history is the source of truth).
      </div>

      {missingBalance.length > 0 ? (
        <div role="note" style={alertBox('warning')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {missingBalance.length} bucket{missingBalance.length === 1 ? '' : 's'} have no entitlement yet
          </div>
          Records still import, but their used/pending can't be applied until you run{' '}
          <strong>Set entitlements</strong> for these people. They show as “no balance” below.
        </div>
      ) : null}

      {preview.bucketUpdates.length > 0 ? (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            Balances after import (recalculated from the kept records)
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th style={th}>Employee</th>
                  <th style={th}>Type</th>
                  <th style={th}>Year</th>
                  <th style={th}>Used (old → new)</th>
                  <th style={th}>Pending (old → new)</th>
                  <th style={th}>Available after import</th>
                </tr>
              </thead>
              <tbody>
                {preview.bucketUpdates.map((b, i) => (
                  <tr key={`${b.employeeName}-${b.leaveType}-${b.year}-${i}`}>
                    <td style={cell}>{b.employeeName}</td>
                    <td style={cell}>{b.leaveType}</td>
                    <td style={cell}>{b.year}</td>
                    <td style={cell}>
                      {b.balanceFound ? (
                        <>
                          {fmt(b.oldUsed)} → <strong>{fmt(b.newUsed)}</strong>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-3)' }}>no balance ({fmt(b.newUsed)})</span>
                      )}
                    </td>
                    <td style={cell}>
                      {b.balanceFound ? (
                        <>
                          {fmt(b.oldPending)} → <strong>{fmt(b.newPending)}</strong>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-3)' }}>no balance ({fmt(b.newPending)})</span>
                      )}
                    </td>
                    <td style={cell}>
                      {b.balanceFound ? (
                        <strong
                          style={{
                            color:
                              Number(b.newAvailable) < 0 ? 'var(--danger-fg)' : 'var(--text)',
                          }}
                        >
                          {fmt(b.newAvailable)} day{fmt(b.newAvailable) === '1' ? '' : 's'}
                        </strong>
                      ) : (
                        <span style={{ color: 'var(--text-3)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div role="note" style={alertBox('info')}>
          No balance buckets will change from the kept records.
        </div>
      )}

      {serverError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Import failed</div>
          {serverError}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={secondaryBtn()} disabled={busy}>
          ← Back
        </button>
        <button
          type="button"
          onClick={handleCommit}
          disabled={busy || preview.wouldCreate === 0}
          style={primaryBtn(busy || preview.wouldCreate === 0)}
        >
          {busy ? 'Importing…' : `Import ${preview.wouldCreate} record${preview.wouldCreate === 1 ? '' : 's'} now`}
        </button>
      </div>
    </div>
  )
}

// ── Step 5: Done ──────────────────────────────────────────────────────────────

interface DoneStepProps {
  preview: HistoryImportResult
  onReset: () => void
}

export function DoneStep({ preview, onReset }: DoneStepProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ padding: '20px 24px', background: 'var(--success-bg)', border: '1px solid var(--success-line)', borderRadius: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--success-fg)', marginBottom: 8 }}>
          Import complete
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-2)' }}>
          Imported {preview.wouldCreate} leave record{preview.wouldCreate === 1 ? '' : 's'} and
          recalculated {preview.bucketUpdates.length} balance bucket
          {preview.bucketUpdates.length === 1 ? '' : 's'}.
          {preview.skipped > 0 ? ` ${preview.skipped} were already present.` : ''}
          {preview.errorCount > 0
            ? ` ${preview.errorCount} row${preview.errorCount === 1 ? '' : 's'} were not imported (had errors).`
            : ''}
        </div>
      </div>

      <div
        role="note"
        style={{ fontSize: 13, color: 'var(--text-2)', padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 8 }}
      >
        Open any employee's profile and check their leave balances — used and pending now reflect the
        imported history.
      </div>

      <div>
        <button type="button" onClick={onReset} style={secondaryBtn()}>
          Import another file
        </button>
      </div>
    </div>
  )
}
